'use client';

import React, { useState, useEffect, useRef } from 'react';
import { HUDData } from '../types/game';
import { ControlMode } from './RunSceneShell';
import ScoreDisplay from './ScoreDisplay';
import ComboIndicator from './ComboIndicator';
import TrickAnnouncer from './TrickAnnouncer';
import SpeedGauge from './SpeedGauge';
import AirMeter from './AirMeter';
import HealthBar from './HealthBar';
import ActionButtons from './ActionButtons';
import TouchSteerButtons from './TouchSteerButtons';

interface GameHUDProps {
  hudData: HUDData;
  controlMode: ControlMode;
  scoreKey: number;
  onPause: () => void;
  onJump: () => void;
  onSpin: () => void;
  onTrick: () => void;
  onCombo: () => void;
  onTiltLeft: () => void;
  onTiltRight: () => void;
}

export default function GameHUD({
  hudData,
  controlMode,
  scoreKey,
  onPause,
  onJump,
  onSpin,
  onTrick,
  onCombo,
  onTiltLeft,
  onTiltRight,
}: GameHUDProps) {
  const [runTimeDisplay, setRunTimeDisplay] = useState('0:00');

  useEffect(() => {
    const mins = Math.floor(hudData.runTimeSeconds / 60);
    const secs = Math.floor(hudData.runTimeSeconds % 60);
    setRunTimeDisplay(`${mins}:${secs.toString().padStart(2, '0')}`);
  }, [hudData.runTimeSeconds]);

  return (
    <div className="absolute inset-0 pointer-events-none z-20">
      {/* ── Top HUD Bar ─────────────────────────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 px-3 pt-safe-top pt-3">
        <div className="flex items-start justify-between gap-2">
          {/* Left — Score + Level */}
          <div className="hud-glass rounded-2xl px-3 py-2 flex flex-col gap-0.5 min-w-[100px]">
            <p
              className="text-[10px] font-medium tracking-widest uppercase"
              style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}
            >
              {hudData.levelName}
            </p>
            <ScoreDisplay score={hudData.score} scoreKey={scoreKey} />
            {hudData.bestScore > 0 && (
              <p className="font-mono-game text-[10px]" style={{ color: '#556677' }}>
                BEST {hudData.bestScore.toLocaleString()}
              </p>
            )}
          </div>

          {/* Center — Time */}
          <div className="hud-glass rounded-2xl px-3 py-2 flex flex-col items-center gap-0.5">
            <p
              className="text-[10px] font-medium tracking-widest uppercase"
              style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}
            >
              Time
            </p>
            <p
              className="font-mono-game text-lg font-bold"
              style={{ color: '#00e5ff' }}
            >
              {runTimeDisplay}
            </p>
          </div>

          {/* Right — Pause + Distance */}
          <div className="flex flex-col items-end gap-2">
            <button
              className="pointer-events-auto hud-glass rounded-xl w-10 h-10 flex items-center justify-center btn-action"
              onClick={onPause}
              aria-label="Pause game"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="6" y="4" width="4" height="16" rx="1" fill="#8899aa" />
                <rect x="14" y="4" width="4" height="16" rx="1" fill="#8899aa" />
              </svg>
            </button>
            <div className="hud-glass rounded-xl px-2 py-1">
              <p className="font-mono-game text-xs" style={{ color: '#556677' }}>
                {hudData.distance}m
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Combo Indicator ─────────────────────────────────────────────── */}
      <div className="absolute top-[88px] left-3">
        <ComboIndicator
          multiplier={hudData.comboMultiplier}
          comboCount={hudData.comboCount}
          windowActive={hudData.comboWindowActive}
          windowProgress={hudData.comboWindowProgress}
        />
      </div>

      {/* ── Health Bar ──────────────────────────────────────────────────── */}
      <div className="absolute top-[88px] right-3">
        <HealthBar health={hudData.health} />
      </div>

      {/* ── Trick Announcer ─────────────────────────────────────────────── */}
      <div className="absolute top-[48%] left-0 right-0 flex justify-center pointer-events-none">
        <TrickAnnouncer
          trickName={hudData.trickName}
          trickPoints={hudData.trickPoints}
          comboMultiplier={hudData.comboMultiplier}
        />
      </div>

      {/* ── Air Meter ───────────────────────────────────────────────────── */}
      {hudData.isAirborne && (
        <div className="absolute left-3" style={{ top: '42%' }}>
          <AirMeter airTime={hudData.airTime} />
        </div>
      )}

      {/* ── Speed Gauge ─────────────────────────────────────────────────── */}
      <div className="absolute right-3" style={{ bottom: '180px' }}>
        <SpeedGauge speed={hudData.speed} />
      </div>

      {/* ── Touch Steer Buttons (touch mode only) ───────────────────────── */}
      {controlMode === 'touch' && (
        <div className="absolute bottom-[180px] left-0 right-0 flex justify-between px-3 pointer-events-auto">
          <TouchSteerButtons onLeft={onTiltLeft} onRight={onTiltRight} />
        </div>
      )}

      {/* ── Action Buttons ──────────────────────────────────────────────── */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-auto">
        <ActionButtons
          isAirborne={hudData.isAirborne}
          comboWindowActive={hudData.comboWindowActive}
          onJump={onJump}
          onSpin={onSpin}
          onTrick={onTrick}
          onCombo={onCombo}
        />
      </div>
    </div>
  );
}